/**
 * RemoteInvitesPanel — FSR-facing result of "Send for remote signing"
 * (BACKLOG-0832, Bill 2026-07-13 Item 23).
 *
 * For each remote signer it shows the emailed secure link and the e-mail delivery
 * status. BACKLOG-1738 (Bill 2026-10-05): the signer opens the link, confirms the
 * e-mail address it was sent to, and receives a one-time code BY E-MAIL. The
 * officer never relays a code; the panel shows none. The link can be copied as a
 * fallback when the invite e-mail did not send.
 */
import { useState } from 'react';
import { CheckCircle2, AlertTriangle, Copy, Mail } from '@retail-ops/panel-shell/fi';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';

export interface RemoteInviteRow {
  signerIndex: number;
  /** The open invite (needed to send a reminder or resend). */
  presentationId?: string;
  /** Loaded after the cart was reopened — the link itself can't be shown again. */
  sentAt?: string;
  cancelled?: boolean;
  name: string;
  email?: string;
  link?: string;
  accessCode?: string;
  emailSent?: boolean;
  expiresAt?: string;
  error?: string;
  /** Live progress from the invite list (BACKLOG-1760). */
  stage?: 'sent' | 'checking-email' | 'signing' | 'finished';
  signedCount?: number;
  /** Documents this signer must sign (known once they open the link). */
  toSignCount?: number;
  lastActivityAt?: string;
}

const STAGE_WORDS: Record<NonNullable<RemoteInviteRow['stage']>, string> = {
  sent: 'Hasn’t opened the link yet',
  'checking-email': 'Opened the link — confirming their code',
  signing: 'Signing now',
  finished: 'Finished signing',
};

function ProgressLine({ row }: { row: RemoteInviteRow }): JSX.Element | null {
  if (!row.stage || row.cancelled) return null;
  const bits = [STAGE_WORDS[row.stage]];
  if (typeof row.toSignCount === 'number' && row.toSignCount > 0) bits.push(`${row.signedCount ?? 0} of ${row.toSignCount} documents signed`);
  else if ((row.signedCount ?? 0) > 0) bits.push(`${row.signedCount} document${row.signedCount === 1 ? '' : 's'} signed`);
  if (row.lastActivityAt && row.stage !== 'sent') {
    bits.push(`last activity ${new Date(row.lastActivityAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`);
  }
  return <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>{bits.join(' · ')}</span>;
}

function CopyButton({ value, label }: { value: string; label: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        });
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        border: '1px solid var(--surface-border)', borderRadius: 6,
        background: 'none', cursor: 'pointer', padding: '3px 8px',
        fontSize: 12, color: 'var(--pcb-blue)',
      }}
      aria-label={`Copy ${label}`}
    >
      <Copy size={12} aria-hidden /> {copied ? 'Copied' : label}
    </button>
  );
}

/**
 * Send reminder / cancel (Bill 2026-10-06: "There is no resend and/or nudge buttons for the Officer in-app").
 * A reminder retires the current link and e-mails a fresh one to the e-mail REGISTERED ON THE CIF — never
 * an address typed here (compliance, Bill 2026-10-06); a wrong address is corrected through Change of
 * Contact, and the next reminder picks it up.
 */
function InviteActions({ row, onUpdated }: { row: RemoteInviteRow; onUpdated: (r: RemoteInviteRow) => void }): JSX.Element | null {
  const client = useApiClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (!row.presentationId || row.cancelled || row.stage === 'finished') return null;

  const send = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    try {
      const res = await client.retail.signing.remote.reissue(row.presentationId!, {
        reminder: true,
      });
      if (!res.success) { setMsg(res.error ?? 'Could not send.'); return; }
      onUpdated({
        ...row,
        ...(res.presentationId ? { presentationId: res.presentationId } : {}),
        ...(res.link ? { link: res.link } : {}),
        ...(res.email ? { email: res.email } : {}),
        emailSent: !!res.emailSent,
        ...(res.expiresAt ? { expiresAt: res.expiresAt } : {}),
      });
      setMsg(res.emailSent ? `New link e-mailed to ${res.email ?? row.email} (the e-mail on the customer’s record). The earlier link no longer works.` : `New link created, but the e-mail did not send${res.emailError ? ` (${res.emailError})` : ''} — copy the link instead.`);
    } catch {
      setMsg('Could not send — network error.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>Sent to {row.email ?? '—'}</span>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void send()}>
          {busy ? 'Sending…' : 'Send reminder'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            const reason = window.prompt('Cancel this invite? The link stops working and the document stays unsigned. Reason:');
            if (reason === null) return;
            setBusy(true); setMsg(null);
            client.retail.signing.remote.cancel(row.presentationId!, reason || 'Cancelled by the officer')
              .then((res) => {
                if (res.success === false) { setMsg(res.error ?? 'Could not cancel.'); return; }
                onUpdated({ ...row, cancelled: true });
                setMsg('Invite cancelled. The link no longer works.');
              })
              .catch(() => setMsg('Could not cancel — network error.'))
              .finally(() => setBusy(false));
          }}
        >
          Cancel invite
        </Button>
      </div>
      {msg && <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>{msg}</span>}
      <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>
        The invite only ever goes to the e-mail registered on the customer’s record. If it’s wrong, correct it with
        Change of Contact (with its documentation and account confirmation), then send a reminder — the fresh link
        goes to the corrected address and the old link stops working.
      </span>
    </div>
  );
}

export function RemoteInvitesPanel({
  invites,
  onInviteUpdated,
}: {
  invites: ReadonlyArray<RemoteInviteRow>;
  onInviteUpdated?: (row: RemoteInviteRow) => void;
}): JSX.Element | null {
  if (invites.length === 0) return null;

  return (
    <section
      aria-label="Remote signing invites"
      style={{
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface-card)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15, color: 'var(--pcb-blue-dark)' }}>Remote signing invites</h3>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-600)' }}>
        A secure link was e-mailed to each signer. When they open it, they confirm their e-mail address and
        we e-mail them a one-time code. There is nothing for you to read to them.
      </p>

      {invites.map((r) => (
        <div
          key={r.signerIndex}
          style={{
            border: '1px solid var(--surface-border)',
            borderRadius: 8,
            padding: 'var(--sp-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>{r.name}</strong>
            {r.cancelled ? (
              <Pill variant="muted">Invite cancelled</Pill>
            ) : r.stage === 'finished' ? (
              <Pill variant="ok"><CheckCircle2 size={12} aria-hidden /> Finished signing</Pill>
            ) : r.error ? (
              <Pill variant="err"><AlertTriangle size={12} aria-hidden /> {r.error}</Pill>
            ) : r.emailSent ? (
              <Pill variant="ok"><CheckCircle2 size={12} aria-hidden /> Email sent</Pill>
            ) : (
              <Pill variant="warn"><Mail size={12} aria-hidden /> Email not sent — share the link manually</Pill>
            )}
          </div>

          {!r.error && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ProgressLine row={r} />
              {!r.link && !r.cancelled && r.stage !== 'finished' && (
                <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                  Invite e-mailed{r.sentAt ? ` ${new Date(r.sentAt).toLocaleString()}` : ''}. The link isn’t shown again after the cart is
                  reopened — “Send reminder” e-mails a fresh one.
                </span>
              )}
              {r.link && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-500)', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.link}
                  </span>
                  <CopyButton value={r.link} label="Copy link" />
                </div>
              )}
              {onInviteUpdated && <InviteActions row={r} onUpdated={onInviteUpdated} />}
            </div>
          )}
        </div>
      ))}

      <Banner tone="info">
        The application is locked while signatures are gathered. It completes automatically once every
        remote signer finishes — you&apos;ll see the accounts activate here.
      </Banner>
    </section>
  );
}

export default RemoteInvitesPanel;
